import BigNumber from 'bignumber.js';
import { AuthBase } from '../AuthBase';
import {
  idlFactory as fusionIDL,
  MyOrderItem,
  SwapExactTokensForTokensRequest,
  SwapTokensForExactTokensRequest,
  _SERVICE as FusionActor
} from '@deland-labs/fusion_client';
import { executeWithLogging } from '../utils/errorLogger';
import { CanisterError } from '../utils/exception';
import { parseToCommon } from '../utils/helper';

export enum SpotOrderType {
  Bid = 1,
  Ask = 2
}

export interface SpotOrder {
  id: bigint;
  type: SpotOrderType;
  volume: BigNumber;
  createdAt: number;
  filled: BigNumber;
  price: number;
}

export enum SwapDirection {
  Bid = 1,
  Ask = 2
}

export interface SwapExactTokensForTokenReq {
  amountOutMin: bigint;
  direction: SwapDirection;
  exactTokenAmount: bigint;
}

export interface SwapTokensForExactTokenReq {
  direction: SwapDirection;
  amountInMax: bigint;
  exactTokenAmount: bigint;
}

export interface SwapLiquidityInfo {
  poolAmount: BigNumber;
  poolVolume: BigNumber;
  poolLP: BigNumber;
  userLP: BigNumber;
}

export interface AddLiquidityReq {
  volume: bigint;
  amount: bigint;
}

export class Fusion extends AuthBase {
  private marketId: string;
  private priceDecimals: number;
  constructor(marketId: string, priceDecimals: number) {
    super();
    this.marketId = marketId;
    this.priceDecimals = priceDecimals;
  }

  /**
   * get liquidity info
   * @returns {Promise<SwapLiquidityInfo>}
   */
  public fetchLiquidityInfo = (
    walletAuth: boolean = false
  ): Promise<SwapLiquidityInfo> => {
    return executeWithLogging(async () => {
      const fusion = await this.createFusionActor(this.marketId, !walletAuth);
      const res = await fusion.get_swap_info();
      if ('Ok' in res) {
        return {
          poolAmount: parseToCommon(res.Ok.pool_amount),
          poolVolume: parseToCommon(res.Ok.pool_volume),
          poolLP: parseToCommon(res.Ok.pool_liquidity),
          userLP: parseToCommon(res.Ok.user_liquidity)
        };
      } else throw new CanisterError(res.Err);
    });
  };

  /**
   * add liquidity
   * @param request add liquidity request
   * @returns
   */
  public addLiquidity = (request: AddLiquidityReq): Promise<bigint> => {
    return executeWithLogging(async () => {
      const fusion = await this.createFusionActor(this.marketId, false);
      const res = await fusion.mint_liquidity(request);
      if ('Ok' in res) return res.Ok.liquidity;
      else throw new CanisterError(res.Err);
    });
  };

  /**
   * remove liquidity
   * @param liquidity liquidity token amount
   * @returns
   */
  public removeLiquidity = (liquidity: bigint) => {
    return executeWithLogging(async () => {
      const fusion = await this.createFusionActor(this.marketId, false);
      const res = await fusion.burn_liquidity({ liquidity });
      if ('Ok' in res) return true;
      else throw new CanisterError(res.Err);
    });
  };

  /**
   * swap
   * @param request swap request
   * @returns
   */
  public swap = (
    request: SwapExactTokensForTokenReq | SwapTokensForExactTokenReq
  ) => {
    const reqETT = request as SwapExactTokensForTokenReq;
    const reqTTE = request as SwapTokensForExactTokenReq;
    return executeWithLogging(async () => {
      const fusion = await this.createFusionActor(this.marketId, false);
      if (reqETT.amountOutMin) {
        const res = await fusion.swap_exact_tokens_for_tokens(
          this.parseSwapETTToOrigin(reqETT)
        );

        if ('Ok' in res) return res.Ok.order_id.id;
        else throw new CanisterError(res.Err);
      }

      if (reqTTE.amountInMax) {
        const res = await fusion.swap_tokens_for_exact_tokens(
          this.parseSwapTTEToOrigin(reqTTE)
        );

        if ('Ok' in res) return res.Ok.order_id.id;
        else throw new CanisterError(res.Err);
      }
    });
  };

  /**
   * cancel all orders
   * @returns {Promise<Boolean>}
   */
  public cancelAllOrders = () => {
    return executeWithLogging(async () => {
      const fusion = await this.createFusionActor(this.marketId, false);
      const res = await fusion.cancel_all_orders();
      if ('Ok' in res) return res.Ok;
      else throw new CanisterError(res.Err);
    });
  };

  /**
   * cancel order
   * @param orderId open order id
   * @returns {Promise<Boolean>}
   */
  public cancelOrder = (orderId: bigint) => {
    return executeWithLogging(async () => {
      const fusion = await this.createFusionActor(this.marketId, false);
      const res = await fusion.cancel_order({ id: orderId });
      if ('Ok' in res) return res.Ok;
      else throw new CanisterError(res.Err);
    });
  };

  /**
   * submit bid limit order
   * @param price price of order
   * @param volume volume of order
   * @returns
   */
  public submitBidLimitOrder = (
    price: bigint,
    volume: bigint
  ): Promise<bigint> => {
    return executeWithLogging(async () => {
      const fusion = await this.createFusionActor(this.marketId, false);
      const res = await fusion.submit_bid_limit_order(price, volume);
      if ('Ok' in res) return res.Ok.order_id.id;
      else throw new CanisterError(res.Err);
    });
  };

  /**
   * submit ask limit order
   * @param price price of order
   * @param volume volume of order
   * @returns
   */
  public submitAskLimitOrder = (
    price: bigint,
    volume: bigint
  ): Promise<bigint> => {
    return executeWithLogging(async () => {
      const fusion = await this.createFusionActor(this.marketId, false);
      const res = await fusion.submit_ask_limit_order(price, volume);
      if ('Ok' in res) return res.Ok.order_id.id;
      else throw new CanisterError(res.Err);
    });
  };

  /**
   * fetch my open orders
   * @returns
   */
  public fetchOpenOrders = () => {
    return executeWithLogging(async () => {
      const fusion = await this.createFusionActor(this.marketId, false);
      const res = await fusion.get_user_orders();
      if ('Ok' in res) return res.Ok.map(this.parseOrder);
      else throw new CanisterError(res.Err);
    });
  };

  /**
   * parse origin order item to spot order
   * @param order
   * @returns
   */
  private parseOrder = (order: MyOrderItem): SpotOrder => {
    return {
      id: order.id.id,
      type:
        'Bid' in order.order_direction ? SpotOrderType.Bid : SpotOrderType.Ask,
      volume: parseToCommon(order.volume),
      createdAt: parseToCommon(order.created_at).toNumber(),
      filled: parseToCommon(order.filled),
      price: parseToCommon(order.price, this.priceDecimals).toNumber()
    };
  };

  private swapDirectionToOrderDirection = (swapDirection: SwapDirection) => {
    switch (swapDirection) {
      case SwapDirection.Ask:
        return { Ask: null };
      case SwapDirection.Bid:
        return { Bid: null };
      default:
        throw new console.error('unknown swap direction');
    }
  };

  /**
   * parse swap exact token to token request to actor origin request
   * @param req
   * @returns {SwapExactTokensForTokensRequest}
   */
  private parseSwapETTToOrigin = (
    req: SwapExactTokensForTokenReq
  ): SwapExactTokensForTokensRequest => {
    return {
      amount_out_min: req.amountOutMin,
      order_direction: this.swapDirectionToOrderDirection(req.direction),
      exact_token: req.exactTokenAmount
    };
  };

  /**
   * parse swap token to exact token request to actor origin request
   * @param req
   * @returns {SwapTokensForExactTokensRequest}
   */
  private parseSwapTTEToOrigin = (
    req: SwapTokensForExactTokenReq
  ): SwapTokensForExactTokensRequest => {
    return {
      amount_in_max: req.amountInMax,
      order_direction: this.swapDirectionToOrderDirection(req.direction),
      exact_token: req.exactTokenAmount
    };
  };

  private createFusionActor = (marketId: string, anonymous: boolean = true) => {
    return this.createActor<FusionActor>(marketId, fusionIDL, anonymous);
  };
}
